import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ItemChangedReason } from "dnd-kit-sortable-tree/dist/types";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import { useConfirm } from "context/ConfirmationDialogContext";
import useUpdateStructuredDocumentContent from "hooks/api/REST/assets/useUpdateStructuredDocumentContent";
import useIsFeatureFlagOn from "hooks/useIsFeatureFlagOn";
import {
  IStructuredDocument,
  IStructuredDocumentSection,
} from "models/assets.models";
import { DRAG_AND_DROP_DEBOUNCE_WAIT_MS } from "screens/Project/screens/StructuredDocument/StructuredDocument.constants";
import { TStructuredDocumentTreeItem } from "screens/Project/screens/StructuredDocument/StructuredDocument.types";
import {
  getConfirmModalContentForDocumentInFinal_deprecated,
  getUpdateConfirmationModalContent,
} from "screens/Project/screens/StructuredDocument/StructuredDocument.utils";
import { DocumentStatus } from "utils/constants/doc.constants";
import { FeatureFlagName } from "utils/constants/featureFlags.constants";
import { getStructuredSectionPath } from "utils/helpers";

/**
 * This creates a Map from path to tree view item. It is used when updating existing sections in
 * order to match the section to the corresponding tree item.
 */
const getTreeSectionsByPath = (
  treeViewItems: TStructuredDocumentTreeItem[],
  sectionMap = new Map<string, TStructuredDocumentTreeItem>(),
) => {
  treeViewItems.forEach((treeViewItem) => {
    sectionMap.set(treeViewItem.path, treeViewItem);

    if (treeViewItem.children && treeViewItem.children.length > 0) {
      getTreeSectionsByPath(treeViewItem.children, sectionMap);
    }
  });

  return sectionMap;
};

/**
 * This takes the items from the format the BE gives and turns it into the format that
 * SortableTree expects.
 */
export const getTreeItemsFromSections = (
  sections: IStructuredDocumentSection[],
  treeViewItemsByPath: Map<string, TStructuredDocumentTreeItem>,
  parentPath?: string,
): TStructuredDocumentTreeItem[] => {
  return sections.map((section, index) => {
    const path = getStructuredSectionPath(parentPath, index);

    const oldTreeViewItem = treeViewItemsByPath.get(path);

    const children =
      section.sections &&
      getTreeItemsFromSections(section.sections, treeViewItemsByPath, path);

    return {
      ...omit(section, "sections"),
      // Sortable tree identity is the UI path; BE identity stays in sectionId.
      id: path,
      sectionId: section.id,
      path,
      // Collapse by default when this is the first time the sections are being processed. If the
      // action done was to add or remove a subsection from a collapsed item, uncollapse it so the
      // user can see the result of the action
      collapsed:
        (oldTreeViewItem?.collapsed ?? (children && children.length > 0)) &&
        (!oldTreeViewItem ||
          oldTreeViewItem.children?.length === children.length),
      ...(children ? { children } : {}),
    };
  });
};

/**
 * This takes the items from the format the sortable tree is using and puts it back into the format
 * that the BE expects.
 */
export const getSectionsFromTreeItems = (
  treeViewItems: TStructuredDocumentTreeItem[],
): IStructuredDocumentSection[] => {
  return treeViewItems.map(
    ({ name, documents, documentsAssets, sectionId, children, depth }) =>
      ({
        name,
        documents,
        documentsAssets,
        ...(sectionId ? { id: sectionId } : {}),
        ...(children && depth !== 6
          ? { sections: getSectionsFromTreeItems(children) }
          : {}),
      }) as IStructuredDocumentSection,
  );
};

export const recalculateTreeItemPaths = (
  treeViewItems: TStructuredDocumentTreeItem[],
  parentPath?: string,
): TStructuredDocumentTreeItem[] =>
  treeViewItems.map((treeViewItem, index) => {
    const path = getStructuredSectionPath(parentPath, index);

    return {
      ...treeViewItem,
      // Keep the sortable adapter id aligned with the recalculated UI path after moves.
      id: path,
      path,
      ...(treeViewItem.children
        ? { children: recalculateTreeItemPaths(treeViewItem.children, path) }
        : {}),
    };
  });

/**
 * When the user clicks on a collapse button in a row, this ensures that all of the children also
 * get recursively collapsed. When the user drops an item onto a collapsed parent as a child, it
 * also expands that section so they can see the result of the drop. Other sub-sections remain
 * collapsed.
 */
export const recursivelyCollapse = (
  treeViewItems: TStructuredDocumentTreeItem[],
  dropParentPath: string | null,
  pathsToCollapse: string[],
  isCollapsed = false,
): TStructuredDocumentTreeItem[] =>
  treeViewItems.map((treeViewItem) => {
    const collapsed =
      treeViewItem.path === dropParentPath
        ? false
        : treeViewItem.collapsed || isCollapsed;

    if (collapsed) {
      pathsToCollapse.push(treeViewItem.path);
    }

    return {
      ...treeViewItem,
      collapsed:
        // Leaf sections with no children have their state managed by react state rather than by
        // SortableTree, so we have to keep their collapsed value as false and set them as collapsed
        // via pathsToCollapse above
        collapsed && treeViewItem.children && treeViewItem.children.length > 0,
      ...(treeViewItem.children
        ? {
            children: recursivelyCollapse(
              treeViewItem.children,
              dropParentPath,
              pathsToCollapse,
              treeViewItem.collapsed || isCollapsed,
            ),
          }
        : {}),
    };
  });

type TUseReorderStructuredDocumentProps = {
  structuredDocument?: IStructuredDocument;
};

const useReorderStructuredDocument = ({
  structuredDocument,
}: TUseReorderStructuredDocumentProps) => {
  const { t } = useTranslation("documents");
  const { attemptConfirm } = useConfirm();

  const isSimplifyContentEnabled = useIsFeatureFlagOn(
    FeatureFlagName.EnableSimplifyContent,
  );

  const contentSections = structuredDocument?.content?.contentSections;

  const [isSortingInProgress, setIsSortingInProgress] = useState(false);

  const [sortStatus, setSortStatus] = useState<null | "sorting" | "saving">(
    null,
  );

  const [treeViewItems, setTreeViewItems] = useState<
    TStructuredDocumentTreeItem[]
  >([]);

  // This keeps the value current for the effect below to use without changes causing the effect to
  // re-run. We only want to re-initialize the items from the template when the document itself
  // changes.
  const treeViewItemsRef = useRef(treeViewItems);
  treeViewItemsRef.current = treeViewItems;

  useEffect(() => {
    const treeViewItemsByPath = getTreeSectionsByPath(treeViewItemsRef.current);

    setTreeViewItems(
      getTreeItemsFromSections(contentSections ?? [], treeViewItemsByPath),
    );
  }, [contentSections]);

  const [expandedSectionPaths, setExpandedSectionPaths] = useState<Set<string>>(
    new Set(),
  );

  const { updateStructuredDocumentContent } =
    useUpdateStructuredDocumentContent({
      successMessage: t("notifications.structuredDocumentOrderUpdateSuccess", {
        ns: "common",
      }),
      failureMessage: t("notifications.structuredDocumentOrderUpdateFailure", {
        ns: "common",
      }),
    });

  const debouncedSaveStructuredDocument = useMemo(
    () =>
      debounce(async (updatedSections: IStructuredDocumentSection[]) => {
        if (!structuredDocument) {
          return;
        }

        try {
          setSortStatus("saving");
          await updateStructuredDocumentContent({
            assetId: structuredDocument?.id ?? "",
            data: { content: JSON.stringify({ sections: updatedSections }) },
          });
        } finally {
          setIsSortingInProgress(false);
          setSortStatus(null);
        }
      }, DRAG_AND_DROP_DEBOUNCE_WAIT_MS),
    [structuredDocument, updateStructuredDocumentContent],
  );

  const handleTreeViewItemsChanged = useCallback(
    async (
      updatedTreeViewItems: TStructuredDocumentTreeItem[],
      reason?: ItemChangedReason<TStructuredDocumentTreeItem>,
    ) => {
      const dropParentPath =
        reason?.type === "dropped" && reason.droppedToParent
          ? reason.droppedToParent.id.toString()
          : null;

      const recalculatedTreeViewItems =
        recalculateTreeItemPaths(updatedTreeViewItems);

      const pathsToCollapse: string[] = [];

      setTreeViewItems(
        recursivelyCollapse(
          recalculatedTreeViewItems,
          dropParentPath,
          pathsToCollapse,
        ),
      );

      setExpandedSectionPaths((existingExpandedSectionPaths) => {
        const updatedExpandedSectionPaths = new Set(
          existingExpandedSectionPaths,
        );

        pathsToCollapse.forEach((path) =>
          updatedExpandedSectionPaths.delete(path),
        );

        if (dropParentPath) {
          updatedExpandedSectionPaths.add(dropParentPath);
        }

        return updatedExpandedSectionPaths;
      });

      const updatedSections = getSectionsFromTreeItems(
        recalculatedTreeViewItems,
      );

      const isDeepEqual = isEqual(updatedSections, contentSections ?? []);

      if (!isDeepEqual) {
        const confirmed =
          structuredDocument?.status !== DocumentStatus.Final
            ? true
            : await attemptConfirm(
                isSimplifyContentEnabled
                  ? getUpdateConfirmationModalContent(
                      t,
                      t("structuredDocument.reorderSectionsModalTitle"),
                    )
                  : getConfirmModalContentForDocumentInFinal_deprecated(
                      t,
                      t("structuredDocument.reorderSectionsModalTitle"),
                    ),
              );

        if (!confirmed) {
          setTreeViewItems(treeViewItems);

          return;
        }

        setSortStatus("sorting");
        setIsSortingInProgress(true);
        debouncedSaveStructuredDocument(updatedSections);
      }
    },
    [
      attemptConfirm,
      contentSections,
      debouncedSaveStructuredDocument,
      isSimplifyContentEnabled,
      structuredDocument?.status,
      treeViewItems,
      t,
    ],
  );

  return {
    isSortingInProgress,
    setIsSortingInProgress,
    sortStatus,
    treeViewItems,
    handleTreeViewItemsChanged,
    expandedSectionPaths,
    setExpandedSectionPaths,
  };
};

export default useReorderStructuredDocument;
